import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DefinedField, DivisionConfig, Play, PlayConfig, PlayGeneratorType } from '@r1-compass-library/models';
import { RoutingService } from '@r1-compass-library/services';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { formValues } from '@r1-compass-library/utilities';
import { signalSlice } from 'ngxtension/signal-slice';
import { combineLatest, from, lastValueFrom, map } from 'rxjs';
import { PlayStore } from '../store/play.store';
import { PlayDefinitionComponent } from './play-definition.component';
import { PlayValidationStateService } from '../play-validation-state.service';

export class DefinitionForm {
    playInternalName = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
    });
    playInternalDescription = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.maxLength(256)],
    });
    playSalesforceName = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
    });
    playSalesforceDescription = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.maxLength(256)],
    });
    recommendationGeneratorType = new FormControl<PlayGeneratorType>('' as PlayGeneratorType, {
        nonNullable: true,
        validators: [Validators.required],
    });
    recommendationLifecycleRulesetId = new FormControl<number | null>(null, { nonNullable: true });
    targeting = new FormControl<string[]>([], { nonNullable: true });
    additionalFields = new FormArray<FormControl<string | boolean>>([]);
    maxRecordsPerAccount = new FormControl<number>(0, { nonNullable: true });
}

export function convertDomainToForm(play: Play, divisionConfig: DivisionConfig): DefinitionFormData {
    return {
        playInternalName: play.name,
        playInternalDescription: play.description,
        playSalesforceName: play.salesforceName ? play.salesforceName : play.name,
        playSalesforceDescription: play.salesforceDescription ? play.salesforceDescription : play.description,
        recommendationGeneratorType: play.type,
        recommendationLifecycleRulesetId: play.lifecycleRulesetId ?? null,
        targeting: convertToRevenueTargetingFormValue(play.config?.definition?.revenueTargeting),
        additionalFields: convertAdditionalFieldsToFormValues(divisionConfig, play),
        maxRecordsPerAccount: play.config?.definition?.maxRecordsPerAccount,
    };

    function convertToRevenueTargetingFormValue(revenueTargeting?: string): string[] {
        if (revenueTargeting) {
            return revenueTargeting.split(' | ');
        } else {
            return [];
        }
    }

    function convertAdditionalFieldsToFormValues(divConfig: DivisionConfig, ply: Play): (string | boolean)[] {
        const divisionAdditionalFields = divConfig.config?.mapping?.additionalFields ?? [];
        const playAdditionalFields = ply.config?.definition?.additionalPlayFields ?? [];
        const values: (string | boolean)[] = [];
        divisionAdditionalFields
            .filter(field => field.source === 'User')
            .forEach(divisionAdditionalField => {
                const matchingPlayAdditionalField = playAdditionalFields.find(
                    field => field.id === divisionAdditionalField.id
                );
                if (divisionAdditionalField.type === 'boolean') {
                    values.push(matchingPlayAdditionalField?.value === 'true');
                } else if (divisionAdditionalField.type === 'picklist') {
                    const matchingPicklist = divisionAdditionalField.picklistElements!.find(
                        element => element.id === matchingPlayAdditionalField?.value
                    );
                    if (matchingPicklist) {
                        values.push(matchingPicklist.id!);
                    } else {
                        values.push(''); //push out a blank so it can be flagged as incorrect in the ui
                    }
                } else {
                    values.push(matchingPlayAdditionalField?.value ?? '');
                }
            });
        return values;
    }
}

export function addAdditionalFieldFormGroups(
    definitionForm: FormGroup<DefinitionForm>,
    divisionAdditionalFields: DefinedField[]
) {
    definitionForm.controls.additionalFields.clear();
    divisionAdditionalFields
        .filter(field => field.source === 'User')
        .forEach(divisionAdditionalField => {
            let formControl: FormControl<string | boolean>;
            if (divisionAdditionalField.type === 'boolean') {
                formControl = new FormControl<string | boolean>(false, {
                    nonNullable: true,
                });
            } else {
                formControl = new FormControl<string | boolean>('', {
                    nonNullable: true,
                });
            }
            if (divisionAdditionalField.required) {
                formControl.addValidators(Validators.required);
            }
            definitionForm.controls.additionalFields.push(formControl);
        });
}

export type DefinitionFormData = {
    playInternalName: string | undefined;
    playInternalDescription: string | undefined;
    playSalesforceName: string | undefined;
    playSalesforceDescription: string | undefined;
    recommendationGeneratorType: PlayGeneratorType | undefined;
    recommendationLifecycleRulesetId: number | null;
    targeting: string[] | undefined;
    additionalFields: (string | boolean)[];
    maxRecordsPerAccount: number | undefined;
};

export type PlayAdditionalFieldFormValue = {
    id: string;
    name: string;
    value: string;
};

@Injectable({ providedIn: PlayDefinitionComponent })
export class PlayDefinitionFormService {
    readonly #routingService = inject(RoutingService);
    readonly #route = inject(ActivatedRoute);
    readonly #playStore = inject(PlayStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #fb = inject(NonNullableFormBuilder);
    readonly #playValidationStateService = inject(PlayValidationStateService);

    // This could be built into the state w/a change of one function if desired
    private formStateMetadata = {
        loaded: signal(false),
    };

    public definitionForm = this.#fb.group(new DefinitionForm());

    private initialState: DefinitionFormData & { submitted: boolean } = {
        playInternalName: undefined,
        playInternalDescription: undefined,
        playSalesforceName: undefined,
        playSalesforceDescription: undefined,
        recommendationGeneratorType: undefined,
        recommendationLifecycleRulesetId: null,
        targeting: [],
        additionalFields: [],
        maxRecordsPerAccount: undefined,
        submitted: false,
    };

    queryParams = toSignal(this.#route.queryParams);

    selectedDivisionConfig$ = toObservable(this.#globalLoggedInUserStore.selectedDivisionConfig);

    divisionConfig: Signal<DivisionConfig | undefined> = this.#globalLoggedInUserStore.selectedDivisionConfig;
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;
    loggedInUserSelectedIamDivisionId$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivisionId);
    formState = signalSlice({
        initialState: this.initialState,
        sources: [
            formValues(this.definitionForm),
            combineLatest([
                this.#playStore.play$,
                this.selectedDivisionConfig$,
                this.loggedInUserSelectedIamDivisionId$,
            ]).pipe(
                map(([play, divisionConfig, selectedIamDivisionId]) => {
                    let formState = this.initialState;
                    const validDivisionMatchOrNewPlay =
                        play?.iamDivisionId === divisionConfig?.iamDivisionId ||
                        selectedIamDivisionId === divisionConfig?.iamDivisionId;
                    if (divisionConfig && validDivisionMatchOrNewPlay) {
                        addAdditionalFieldFormGroups(
                            this.definitionForm,
                            divisionConfig.config?.mapping?.additionalFields ?? []
                        );

                        if (play && play.iamDivisionId === divisionConfig.iamDivisionId) {
                            formState = {
                                ...convertDomainToForm(play, divisionConfig),
                                submitted: false,
                            };
                            this.definitionForm.patchValue(formState);
                            this.formStateMetadata.loaded.set(true);
                            this.definitionForm.markAllAsTouched();
                        }
                    }
                    return formState;
                })
            ),
        ],
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        effects: state => ({
            init: () => {
                if (this.#routingService.assetId() === 'new') {
                    const queryParams = this.queryParams();
                    if (queryParams) {
                        const generatorType: PlayGeneratorType = queryParams['generatorType'];
                        this.definitionForm.controls.recommendationGeneratorType.setValue(generatorType);
                        this.definitionForm.controls.recommendationGeneratorType.disable();
                        if (generatorType === 'Importer') {
                            this.definitionForm.controls.recommendationLifecycleRulesetId.disable();
                            this.definitionForm.controls.maxRecordsPerAccount.disable();
                        } else {
                            this.definitionForm.controls.recommendationLifecycleRulesetId.addValidators(
                                Validators.required
                            );
                            this.definitionForm.controls.recommendationLifecycleRulesetId.updateValueAndValidity({
                                emitEvent: false,
                            });
                        }
                    }
                }
            },
            afterFormPopulated: () => {
                const loaded = this.formStateMetadata.loaded();
                if (loaded) {
                    this.definitionForm.controls.recommendationGeneratorType.disable();
                    if (this.definitionForm.controls.recommendationGeneratorType.value === 'Importer') {
                        this.definitionForm.controls.recommendationLifecycleRulesetId.disable();
                        this.definitionForm.controls.maxRecordsPerAccount.disable();
                    } else {
                        this.definitionForm.controls.recommendationLifecycleRulesetId.addValidators(
                            Validators.required
                        );
                        this.definitionForm.controls.recommendationLifecycleRulesetId.updateValueAndValidity({
                            emitEvent: false,
                        });
                    }
                }
            },
            validateMaxRecordsPerAccount: () => {
                const divisionConfig = this.#globalLoggedInUserStore.selectedDivisionConfig();

                const multipleRecData = divisionConfig?.config?.mapping?.multipleRecommendations;
                if (
                    multipleRecData?.enabled &&
                    this.definitionForm.controls.recommendationGeneratorType.value === 'Builder'
                ) {
                    this.definitionForm.controls.maxRecordsPerAccount.setValidators(Validators.required);
                }
            },
        }),
    });

    public submit() {
        let play: Partial<Play> | undefined = this.#playStore.play();
        if (play) {
            //we strip out the status because we don't want to update it with stale data
            play.status = undefined;
        } else {
            play = this.createNewPlay();
        }

        play.valid = this.#playValidationStateService.determinePlayValidity('definition', play);

        return lastValueFrom(from(this.savePlayAndData(play)).pipe(map(() => ({ submitted: true }))));
    }

    private divisionId = computed(() => this.divisionConfig()?.iamDivisionId);

    private createNewPlay(): Partial<Play> {
        return {
            enabled: false,
            valid: false,
            status: 'Pending',
            folderId: this.checkForRootFolder(),
            appId: 1,
            createdBy: this.loggedInUserId(),
        };
    }

    /**
     * Method for determining if asset is in Root folder or not.
     * Returns "undefined" if folderId is 0, as 0 is assumed to be Root.
     * Non-folder assets cannot have folderIds if they are created in Root, or else they will error due to database config.
     * @param id The current folderId
     * @private
     */
    private checkForRootFolder() {
        const queryParams = this.queryParams();
        const id = queryParams ? Number(queryParams['folderId']) : 1;
        if (id === 0) {
            return undefined;
        } else {
            return id;
        }
    }

    private savePlayAndData(play: Partial<Play>) {
        const formValue = this.definitionForm.getRawValue();
        const savePlay: Partial<Play> = {
            ...play,
            name: formValue.playInternalName,
            description: formValue.playInternalDescription,
            salesforceName: formValue.playSalesforceName,
            salesforceDescription: formValue.playSalesforceDescription,
            iamDivisionId: this.divisionId(),
            type: formValue.recommendationGeneratorType,
            lifecycleRulesetId: formValue.recommendationLifecycleRulesetId,
            updatedBy: this.loggedInUserId(),
            lastConfigured: new Date(),
        };
        const saveConfig: Partial<PlayConfig> = {
            definition: {
                ...play?.config?.definition,
                revenueTargeting: this.formatPlayCmts(formValue.targeting ?? []),
                additionalPlayFields: this.convertToDefinedFields(this.definitionForm.controls.additionalFields),
                maxRecordsPerAccount: formValue.maxRecordsPerAccount,
            },
        };
        return this.#playStore.savePlayAndData({ play: savePlay, config: saveConfig });
    }

    private convertToDefinedFields(
        additionalFieldsFormArray: FormArray<FormControl<string | boolean>>
    ): DefinedField[] {
        const userDefinedFields = this.#globalLoggedInUserStore
            .selectedDivisionConfig()
            ?.config?.mapping?.additionalFields?.filter(field => field.source === 'User');
        if (userDefinedFields) {
            additionalFieldsFormArray.controls.forEach((additionalFieldForm, index) => {
                let definedFieldValue = String(additionalFieldForm.value);
                if (additionalFieldForm.value === null || additionalFieldForm.value === undefined) {
                    definedFieldValue = additionalFieldForm.value; // don't construct String from nullish values
                }
                userDefinedFields.at(index)!.value = definedFieldValue;
            });
        }
        return userDefinedFields ?? [];
    }

    private formatPlayCmts(playCmts: string[]): string {
        if (playCmts.length) {
            let pipeDelimitedString = '';
            for (let i = 0; i < playCmts.length - 1; i++) {
                pipeDelimitedString = pipeDelimitedString + playCmts[i] + ' | ';
            }
            pipeDelimitedString = pipeDelimitedString + playCmts[playCmts.length - 1];
            return pipeDelimitedString;
        } else {
            return '';
        }
    }
}
